<script setup>
/**
 * Modal "Planes" · catálogo showroom (Victor 2026-06-07 · Propuesta 1).
 * Muestra los planes del catálogo como una página de precios profesional, con
 * filtros por categoría, sucursal y método de pago + búsqueda. Cada "Reservar →"
 * emite el plan para abrir el formulario de reserva ya precargado.
 */
import { ref, computed, watch, onMounted } from 'vue';
import ReservationPlansAPI from 'dashboard/api/reservationPlans';
import BranchesAPI from 'dashboard/api/branches';

const props = defineProps({
  show: { type: Boolean, default: false },
  // embedded: se muestra DENTRO de una página (Planes en el menú) en vez de como
  // overlay a pantalla completa. Sin teleport, sin botón cerrar (Victor 2026-06-07).
  embedded: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'view', 'reserve']);

const CAT_LABELS = {
  quince: 'Quinceañera', ninos: 'Niños/as', boda: 'Boda', embarazo: 'Embarazo',
  graduacion: 'Graduación', familiar: 'Familiar', empresarial: 'Empresarial',
  madre: 'Madre', recien_nacido: 'Recién Nacido', cumpleanos: 'Cumpleaños',
  mellizas: 'XV Mellizas', basico: 'Básico de Estudio', otro: 'Otro',
};

const plans = ref([]);
const branches = ref([]);
const loading = ref(false);

const search = ref('');
const catFilter = ref('all');
const branchFilter = ref('all');
const payFilter = ref('all');

const fmt = n =>
  `RD$ ${(Number(n) || 0).toLocaleString('es-DO', { maximumFractionDigits: 0 })}`;
const catLabel = c => CAT_LABELS[c] || c || 'Otro';

const load = async () => {
  loading.value = true;
  try {
    const [p, b] = await Promise.all([
      ReservationPlansAPI.get(),
      BranchesAPI.get(),
    ]);
    plans.value = p.data || [];
    branches.value = b.data || [];
  } catch (e) {
    plans.value = [];
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.show,
  show => {
    if (show) {
      search.value = '';
      catFilter.value = 'all';
      branchFilter.value = 'all';
      payFilter.value = 'all';
      load();
    }
  }
);

// Embebido (Informes → Planes): show es true desde el montaje, el watch no
// dispara, así que cargamos aquí.
onMounted(() => {
  if (props.embedded || props.show) load();
});

// Categorías presentes en el catálogo (para los chips superiores).
const categories = computed(() => {
  const set = [...new Set(plans.value.map(p => p.category).filter(Boolean))];
  return set.map(c => ({ value: c, label: catLabel(c) }));
});

const filteredPlans = computed(() => {
  const q = search.value.trim().toLowerCase();
  return plans.value.filter(p => {
    if (p.active === false) return false;
    if (catFilter.value !== 'all' && p.category !== catFilter.value) return false;
    if (branchFilter.value !== 'all') {
      const ids = Array.isArray(p.branch_ids) ? p.branch_ids : [];
      if (ids.length && !ids.includes(branchFilter.value)) return false;
    }
    if (payFilter.value !== 'all') {
      const pm = Array.isArray(p.payment_methods) ? p.payment_methods : [];
      if (pm.length && !pm.includes(payFilter.value)) return false;
    }
    if (q && !`${p.name} ${catLabel(p.category)}`.toLowerCase().includes(q)) return false;
    return true;
  });
});

// Construye las líneas de características de un plan a partir de sus campos.
const features = p => {
  const f = [];
  if (p.photos != null && p.photos !== '') f.push({ ic: '🖼', t: `${p.photos} fotografías editadas y retocadas` });
  if (p.minutes != null && p.minutes !== '') f.push({ ic: '🕐', t: `${p.minutes} minutos de sesión` });
  if (p.scenarios != null && p.scenarios !== '') {
    const pre = p.scenarios_up_to ? 'Hasta ' : '';
    f.push({ ic: '🖥', t: `${pre}${p.scenarios} escenario${p.scenarios > 1 ? 's' : ''} a elegir` });
  }
  if (p.outfits != null && p.outfits !== '') f.push({ ic: '👗', t: `${p.outfits} vestimenta${p.outfits > 1 ? 's' : ''}` });
  if (p.max_people != null && p.max_people !== '') f.push({ ic: '👥', t: `Máximo ${p.max_people} persona${p.max_people > 1 ? 's' : ''}` });
  return f;
};

const onReserve = plan => emit('reserve', plan);
const onView = plan => emit('view', plan);
</script>

<template>
  <teleport to="body" :disabled="embedded">
    <div v-if="show || embedded" class="ps-fs" :class="{ 'ps-embedded': embedded }">
      <!-- header -->
      <div class="ps-head">
        <div class="ps-logo">
          <div class="ps-lg">📷</div>
          <div><div class="ps-a">VICTOR GUZMÁN</div><div class="ps-b">FOTOGRAFÍA · PLANES</div></div>
        </div>
        <button v-if="!embedded" class="ps-x" @click="emit('close')">✕</button>
      </div>

      <!-- categorías -->
      <div v-if="categories.length" class="ps-cats">
        <button class="ps-cat" :class="{ on: catFilter === 'all' }" @click="catFilter = 'all'">Todos</button>
        <button
          v-for="c in categories"
          :key="c.value"
          class="ps-cat"
          :class="{ on: catFilter === c.value }"
          @click="catFilter = c.value"
        >{{ c.label }}</button>
      </div>

      <!-- filtros -->
      <div class="ps-filters">
        <div class="ps-search">🔎 <input v-model="search" type="text" placeholder="Buscar plan…" /></div>
        <div class="ps-fg">
          <span class="ps-fl">Sucursal</span>
          <button class="ps-chip" :class="{ on: branchFilter === 'all' }" @click="branchFilter = 'all'">Todas</button>
          <button
            v-for="b in branches"
            :key="b.id"
            class="ps-chip"
            :class="{ on: branchFilter === b.id }"
            @click="branchFilter = b.id"
          >{{ b.code || b.name }}</button>
        </div>
        <div class="ps-fg">
          <span class="ps-fl">Pago</span>
          <button class="ps-chip" :class="{ on: payFilter === 'all' }" @click="payFilter = 'all'">Todos</button>
          <button class="ps-chip pay" :class="{ on: payFilter === 'Efectivo' }" @click="payFilter = 'Efectivo'">💵 Efectivo</button>
          <button class="ps-chip pay" :class="{ on: payFilter === 'Transferencia' }" @click="payFilter = 'Transferencia'">🏦 Transferencia</button>
          <button class="ps-chip pay" :class="{ on: payFilter === 'Tarjeta' }" @click="payFilter = 'Tarjeta'">💳 Tarjeta</button>
        </div>
      </div>

      <!-- grid -->
      <div class="ps-body">
        <div v-if="loading" class="ps-state">Cargando planes…</div>
        <div v-else-if="!filteredPlans.length" class="ps-state">
          No hay planes con esos filtros. Agrégalos en Ajustes → Catálogo de Planes.
        </div>
        <div v-else class="ps-grid">
          <div v-for="(p, i) in filteredPlans" :key="p.id" class="ps-plan" :class="{ feat: p.featured }">
            <div v-if="p.featured" class="ps-star">⭐ Recomendado</div>
            <div class="ps-top">
              <div class="ps-badge"><div class="l">PLAN</div><div class="n">{{ i + 1 }}</div></div>
              <div><div class="ps-title">{{ p.name }}</div><div class="ps-cat-l">{{ catLabel(p.category) }}</div></div>
            </div>
            <div class="ps-feat">
              <div v-for="(f, fi) in features(p)" :key="fi" class="ps-f"><span class="ps-fic">{{ f.ic }}</span> {{ f.t }}</div>
              <div v-if="!features(p).length" class="ps-f ps-empty">Sin detalles cargados</div>
            </div>
            <div v-if="(p.payment_methods || []).length" class="ps-pays">
              <span v-for="m in p.payment_methods" :key="m" class="ps-paytag">{{ m }}</span>
            </div>
            <div class="ps-foot">
              <div class="ps-price"><small>RD$</small> {{ (Number(p.price) || 0).toLocaleString('es-DO', { maximumFractionDigits: 0 }) }}</div>
              <button class="ps-ver" @click="onView(p)">👁 Ver</button>
              <button class="ps-resv" @click="onReserve(p)">📅 Reservar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style lang="scss" scoped>
.ps-fs { position: fixed; inset: 0; z-index: 10000; display: flex; flex-direction: column; background: radial-gradient(1100px 500px at 80% -10%, rgba(201, 163, 90, 0.06), transparent 60%), #070708; }
/* embebido en la página de Informes → Planes (no overlay) */
.ps-embedded { position: static; inset: auto; z-index: auto; height: calc(100vh - 24px); min-height: 520px; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; overflow: hidden; }
.ps-head { display: flex; align-items: center; gap: 14px; padding: 18px 24px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.ps-logo { display: flex; align-items: center; gap: 11px; }
.ps-lg { width: 42px; height: 42px; border-radius: 11px; background: linear-gradient(135deg, #1c2433, #0d1118); border: 1px solid rgba(201, 163, 90, 0.5); display: grid; place-items: center; font-size: 16px; color: #dcc28d; }
.ps-a { font-size: 16px; font-weight: 800; letter-spacing: 0.5px; color: #fff; }
.ps-b { font-size: 10px; letter-spacing: 3px; color: #c8843e; font-weight: 700; }
.ps-x { margin-left: auto; width: 34px; height: 34px; border-radius: 9px; border: 1px solid rgba(255, 255, 255, 0.08); background: #0d0d0f; color: #8b8b91; cursor: pointer; font-size: 15px; }
.ps-cats { display: flex; gap: 7px; padding: 12px 24px 0; flex-wrap: wrap; }
.ps-cat { font-size: 12.5px; font-weight: 700; color: #8b8b91; padding: 8px 14px; border-radius: 10px; border: 1px solid rgba(255, 255, 255, 0.08); background: none; cursor: pointer; }
.ps-cat.on { color: #dcc28d; background: rgba(201, 163, 90, 0.1); border-color: rgba(201, 163, 90, 0.45); }
.ps-filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 14px 24px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.ps-search { display: flex; align-items: center; gap: 8px; background: #0d0d0f; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 9px 13px; font-size: 12.5px; color: #62626a; min-width: 230px; }
.ps-search input { background: none; border: none; outline: none; color: #ededf0; font-size: 12.5px; width: 100%; }
.ps-fg { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.ps-fl { font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase; color: #62626a; font-weight: 800; }
.ps-chip { font-size: 11.5px; font-weight: 700; color: #8b8b91; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 999px; padding: 6px 12px; cursor: pointer; background: none; }
.ps-chip.on { color: #11141c; background: linear-gradient(180deg, #dcc28d, #c9a35a); border-color: transparent; }
.ps-chip.pay { color: #dcc28d; border-color: rgba(201, 163, 90, 0.45); }
.ps-chip.pay.on { color: #11141c; }
.ps-body { flex: 1; overflow: auto; padding: 18px 24px 28px; }
.ps-state { text-align: center; color: #8b8b91; padding: 50px 20px; font-size: 13.5px; }
.ps-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); gap: 16px; max-width: 1400px; margin: 0 auto; }
.ps-plan { position: relative; background: linear-gradient(180deg, #141416, #0d0d0f); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 15px; overflow: hidden; display: flex; flex-direction: column; transition: transform 0.14s, border-color 0.14s; }
.ps-plan:hover { transform: translateY(-3px); border-color: rgba(201, 163, 90, 0.45); }
.ps-plan.feat { border-color: rgba(201, 163, 90, 0.5); }
.ps-star { position: absolute; top: 10px; right: 10px; font-size: 9px; font-weight: 800; color: #11141c; background: linear-gradient(180deg, #dcc28d, #c9a35a); border-radius: 6px; padding: 3px 8px; }
.ps-top { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.05); }
.ps-badge { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 52px; height: 52px; border-radius: 12px; background: linear-gradient(160deg, #1b1f2a, #0e1016); border: 1px solid rgba(201, 163, 90, 0.45); flex: none; }
.ps-badge .l { font-size: 7.5px; letter-spacing: 1px; color: #62626a; font-weight: 800; }
.ps-badge .n { font-size: 22px; font-weight: 800; color: #dcc28d; line-height: 1; }
.ps-title { font-size: 13.5px; font-weight: 800; color: #fff; }
.ps-cat-l { font-size: 10.5px; color: #c8843e; font-weight: 700; letter-spacing: 0.5px; }
.ps-feat { display: flex; flex-direction: column; gap: 9px; padding: 14px 16px; flex: 1; }
.ps-f { display: flex; align-items: center; gap: 10px; font-size: 12.5px; color: #dcdce0; }
.ps-f.ps-empty { color: #62626a; font-style: italic; }
.ps-fic { width: 26px; height: 26px; border-radius: 8px; border: 1px solid rgba(201, 163, 90, 0.45); display: grid; place-items: center; font-size: 12px; color: #dcc28d; flex: none; }
.ps-pays { display: flex; gap: 5px; padding: 0 16px 14px; flex-wrap: wrap; }
.ps-paytag { font-size: 9px; font-weight: 700; color: #8b8b91; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 6px; padding: 3px 7px; }
.ps-foot { padding: 14px 16px; border-top: 1px solid rgba(255, 255, 255, 0.05); display: flex; align-items: center; gap: 10px; }
.ps-price { font-size: 22px; font-weight: 800; color: #dcc28d; }
.ps-price small { font-size: 12px; color: #8b8b91; font-weight: 600; }
.ps-ver { margin-left: auto; font-size: 12px; font-weight: 800; color: #cfcfd6; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 9px; padding: 9px 12px; cursor: pointer; }
.ps-ver:hover { color: #dcc28d; border-color: rgba(201, 163, 90, 0.45); }
.ps-resv { font-size: 12px; font-weight: 800; color: #11141c; background: linear-gradient(180deg, #dcc28d, #c9a35a); border: none; border-radius: 9px; padding: 9px 14px; cursor: pointer; }
</style>
